import React, { FC, useMemo, useState } from 'react';
import TablePageLayout from '../../components/tablePageLayout/TablePageLayout';
import { useTranslation } from 'react-i18next';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { DoctorStudyListCounter, IStudyDto } from '../../services/api/api-client';
import { AppTable } from 'uikit/table/AppTable';
import { useStudiesColumns } from './study.columns';
import { Tabs } from 'uikit/tabs/tabs';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useGetDoctorStudiesQuery } from 'src/services/api/api-client/StudyQuery';
import { DecodedValueMap, SetQuery, useQueryParams } from 'use-query-params';
import { createPagingParams, pagingSortingToBackendRequest } from 'src/helpers/pagination-helper';
import { studyFilterParams, studyFilterToBackendRequest } from '../adminDashboard/StudyList/study-helpers';
import _ from 'lodash';

type StudyState = 'Active' | 'Closed';

export const studyQueryParamsConfig = {
  ...createPagingParams({
    defaultPerPage: 30,
    defaultSortBy: 'id',
    sortingKeys: ['id'] as const,
    defaultDesc: false,
  }),
  ...studyFilterParams(),
};

export const Studies = () => {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useQueryParams(studyQueryParamsConfig);
  const studiesQuery = useGetDoctorStudiesQuery(
    {
      ...pagingSortingToBackendRequest(queryParams),
      ...studyFilterToBackendRequest(queryParams),
      searchQuery: queryParams.searchQuery,
    },
    { keepPreviousData: false, refetchOnMount: 'always' },
  );

  const rowDataTable = useReactTable<IStudyDto>({
    data: studiesQuery.data?.studies.data ?? [],
    columns: useStudiesColumns(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TablePageLayout testId={'App'} loading={studiesQuery.isLoading}>
      <TablePageLayout.Header>
        <TablePageLayout.Header.Row>
          <TablePageLayout.Header.Title
            text={t('StudiesList.Caption')}
            secondaryText={studiesQuery.data?.counters.all?.toString()}
          />
        </TablePageLayout.Header.Row>
        <StudyFilters counts={studiesQuery.data?.counters} queryParams={queryParams} setQueryParams={setQueryParams} />
      </TablePageLayout.Header>
      <TablePageLayout.TableContainer>
        <AppTable table={rowDataTable} placeholder={{ text: t('StudiesList.NoData'), hide: studiesQuery.isLoading }} />
        <TablePageLayout.TableContainer.Pagination
          page={queryParams.page}
          perPage={queryParams.perPage}
          totalCount={studiesQuery.data?.studies.totalCount ?? 0}
          changePagination={setQueryParams}
        />
      </TablePageLayout.TableContainer>
    </TablePageLayout>
  );
};

type StudyFiltersProps = {
  counts?: DoctorStudyListCounter;
  queryParams: DecodedValueMap<typeof studyQueryParamsConfig>;
  setQueryParams: SetQuery<typeof studyQueryParamsConfig>;
};

const StudyFilters: FC<StudyFiltersProps> = ({ counts, queryParams, setQueryParams }) => {
  const { t } = useScopedTranslation('StudiesList');
  const [currentTab, setStudyState] = useState<StudyState>(queryParams.isClosed ? 'Closed' : 'Active');

  const debounceFunc = _.debounce(
    (value: string | undefined) => setQueryParams({ searchQuery: value, page: undefined }),
    200,
  );

  const tabs = useMemo(
    () => [
      {
        text: t('Tabs.Active', { value: counts?.active || 0 }),
        isSelected: currentTab === 'Active',
        onClick: () => {
          setStudyState('Active');
          setQueryParams({
            isClosed: false,
          });
        },
        testId: 'Active',
      },
      {
        text: t('Tabs.Closed', { value: counts?.closed || 0 }),
        isSelected: currentTab === 'Closed',
        onClick: () => {
          setStudyState('Closed');
          setQueryParams({
            isClosed: true,
          });
        },
        testId: 'Closed',
      },
    ],
    [counts?.active, counts?.closed, currentTab, setQueryParams, t],
  );

  return (
    <div style={{ display: 'contents' }} data-test-id={'patient-filters-container'}>
      <TablePageLayout.Header.Row>
        <Tabs items={tabs} />
      </TablePageLayout.Header.Row>
      <TablePageLayout.Header.Row>
        <TablePageLayout.Header.Search
          defaultValue={queryParams.searchQuery || ''}
          type={'search'}
          placeholder={t('SearchPlaceholder')}
          onChange={(e) => {
            e.preventDefault();
            debounceFunc(e.target.value || undefined);
          }}
        />
      </TablePageLayout.Header.Row>
    </div>
  );
};
